// import { legacy_createStore as createStore } from "redux";
import { StoreEnhancer, configureStore } from "@reduxjs/toolkit";
import { useSelector, TypedUseSelectorHook } from "react-redux";
import * as Sentry from "@sentry/react";
// import { devToolsEnhancer } from "@redux-devtools/extension";
// import baseReducer from "./reducers/baseReducer";
import { NODE_ENV } from "./config/app.config";
import spinnerReducer, { SpinnerReducerState } from "./reducers/spinnerReducer";
import userReducer, { UserReducerState } from "./reducers/userReducer";
import uiSliceReducer, { UiReducerState } from "./reducers/uiReducer";
import pageOverlayReducer, { PageOverlayReducerState } from "./reducers/pageOverlayReducer";

import LocalImageFilesReducer, { LocalImageFilesState } from "./reducers/localImageFilesReducer";
import ProductsReducer, { ProductItemsState } from "./reducers/ProductGroupManagementReducers/ProductsReducer";
import ProductsUiReducer, { ProductsUiState } from "./reducers/ProductGroupManagementReducers/ProductsUiReducer";
import PartnersReducer, { PartnersState } from "./reducers/ProductGroupManagementReducers/PartnersReducer";
import partnersUiReducer, { PartnersUiState } from "./reducers/ProductGroupManagementReducers/PartnersUiReducer";
import productGroupsReducer, { ProductGroupState } from "./reducers/ProductGroupManagementReducers/ProductGroupsReducer";
import productGroupsUiReducer, { ProductGroupsUiState } from "./reducers/ProductGroupManagementReducers/ProductGroupsUiReducer";
import carouselScrollUpdateReducer, { TCarouselScrollUpdateState } from "./reducers/CarouselScrollReducer/CarouselScrollReducer";
import outgoingPartnershipInvitationsReducer, {
    TOutgoingPartnershipInvitationsReducerState,
} from "./reducers/OutgoingPartnershipInvitationsReducer.ts/outgoingPartnershipInvitationsReducer";
import cohortDefinitionsMetadataReducer, { ICohortDefinitionsMetadataState } from "./reducers/cohortDefinitionsReducer";
import recaptchaStatusReducer, { TRecaptchaStatusState } from "./reducers/RecaptchaStatusReducer";
import googleMapsStatusReducer, { TGoogleMapsStatusState } from "./reducers/GoogleMapsStatusReducer";

export interface BaseReducerState {
    spinnerReducer: SpinnerReducerState;
    userReducer: UserReducerState;
    localImageFiles: LocalImageFilesState;
    productsReducer: ProductItemsState;
    productsUiReducer: ProductsUiState;
    partnersReducer: PartnersState;
    partnersUiReducer: PartnersUiState;
    productGroupsReducer: ProductGroupState;
    productGroupsUiReducer: ProductGroupsUiState;
    pageOverlayReducer: PageOverlayReducerState;
    uiSliceReducer: UiReducerState;
    carouselScrollUpdateReducer: TCarouselScrollUpdateState;
    outgoingPartnershipInvitationsReducer: TOutgoingPartnershipInvitationsReducerState;
    cohortDefinitionsMetadata: ICohortDefinitionsMetadataState;
    recaptchaStatusReducer: TRecaptchaStatusState;
    googleMapsStatusReducer: TGoogleMapsStatusState;
}

export const rootReducer = {
    spinnerReducer,
    userReducer,
    localImageFiles: LocalImageFilesReducer,
    productsReducer: ProductsReducer,
    productsUiReducer: ProductsUiReducer,
    partnersReducer: PartnersReducer,
    partnersUiReducer,
    productGroupsReducer,
    productGroupsUiReducer,
    pageOverlayReducer,
    uiSliceReducer,
    carouselScrollUpdateReducer,
    outgoingPartnershipInvitationsReducer,
    cohortDefinitionsMetadata: cohortDefinitionsMetadataReducer,
    recaptchaStatusReducer,
    googleMapsStatusReducer,
};

/** Creates a fresh Redux store without Sentry enhancer — use in tests to avoid cross-test state leakage. */
export const createTestStore = (preloadedState?: Partial<BaseReducerState>) =>
    configureStore({
        reducer: rootReducer,
        preloadedState: preloadedState as BaseReducerState | undefined,
        middleware: (defaultMiddleware) =>
            defaultMiddleware({
                serializableCheck: {
                    ignoredActions: ["localImageFiles/add"],
                    ignoredPaths: ["localImageFiles.localImageFiles"],
                },
            }),
    });

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const store = configureStore<BaseReducerState>({
    reducer: rootReducer,
    middleware: (defaultMiddleware) =>
        defaultMiddleware({
            /**
             * @see https://stackoverflow.com/questions/59944219/non-serializable-file-object-in-redux-action
             * even though files are technically serializable, storing files in redux is not exactly advisable
             */
            serializableCheck: {
                ignoredActions: ["localImageFiles/add"],
                ignoredPaths: ["localImageFiles.localImageFiles"],
            },
        }),
    enhancers:
        NODE_ENV === "development" ? undefined : (defaultEnhancers) => defaultEnhancers({ autoBatch: true }).concat(sentryReduxEnhancer as StoreEnhancer),

    devTools: NODE_ENV === "development",
});

export default store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// https://react-redux.js.org/using-react-redux/static-typing#typing-the-useselector-hook
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
